<script>
  import debounce from "lodash/debounce";

  import { SEARCH } from "../../Utils/utilConstants.svelte";
  import { Loader } from "./index.svelte";

  import { v4 } from "uuid";
  import { loggedInDoctorDetail, user } from "../../stores";
  import {
    getDisplaySurgeonName,
    showException,
  } from "../../Utils/utilFunctions.svelte";
  import { apiMethods } from "../../Services/index.svelte";

  export let title = "Surgeon";
  export let inputID = v4();
  export let checkboxClass = "";
  export let inputClass = "";
  export let titleClass = "";
  export let isDisabled = false;
  export let onChangeFunc = () => {};
  export let selectedSurgeonIds = [];
  export let scene = "";
  export let customizedPlaceholder = "";
  export let showCollapsedPlaceholder = true;
  export let zIndex;
  export let showSelected = false;
  export let showOptionsWidthClass = "max-w-[328px]";
  export let selectedDoctors = [];
  export let searchSurgeonInNoGapProgram = false;

  $: if (selectedSurgeonIds.length) _updateSearchPlaceholder();

  $: marginTop =
    scene.toLowerCase() === "calendar"
      ? "mt-[70px]"
      : titleClass.includes("hidden")
        ? "mt-[30px]"
        : "mt-[50px]";

  $: containerID = inputID + "Container";

  $: window.addEventListener("click", (e) => {
    if (
      (!document.getElementById(`${containerID}`) ||
        !document.getElementById(`${containerID}`).contains(e.target)) &&
      e.target.id !== inputID
    )
      isOptionsListOpen = false;
  });

  $: inputPlaceholder = customizedPlaceholder
    ? customizedPlaceholder
    : showCollapsedPlaceholder
      ? `${
          selectedSurgeonIds.length
            ? `${
                selectedSurgeonIds.length == 1
                  ? allSurgeons.find(
                      ({ uniqueId }) => uniqueId === selectedSurgeonIds[0],
                    )
                    ? getDisplaySurgeonName(
                        allSurgeons.find(
                          ({ uniqueId }) => uniqueId === selectedSurgeonIds[0],
                        ),
                      )
                    : "1 selected"
                  : `${selectedSurgeonIds.length} selected`
              }`
            : ""
        }`
      : defaultPlaceholder;

  let isOptionsListOpen = false,
    defaultPlaceholder = "",
    allSurgeons = [],
    searchKeyWord = "",
    loadingMoreIconEle,
    isSearchingMore,
    isSearching = false;

  export function clearSearchKeyword() {
    searchKeyWord = "";
  }

  let searchIndex = Number(0),
    loadedSurgeonsDoctorId,
    showLoadingMoreIcon = false;

  const SEARCH_GAP = Number(25);

  const defaultCheckboxClass =
    "checkbox checkbox-secondary checkbox-xs cursor-pointer";
  const defaultInputClass =
    "input-sm w-48 bg-gray-200 cursor-default pr-[30px]";
  const defaultTitleClass = "text-[11px] font-[500]";

  $: isUserDoctor = $user.role == 2;

  const searchAccountDoctorsDebounce = debounce(async () => {
    if (isSearching || isSearchingMore) return;
    searchIndex = Number(0);
    showLoadingMoreIcon = false;
    await searchAccountDoctors();
  }, SEARCH.DEBOUNCE_TIME);

  const searchAccountDoctors = async () => {
    if (isUserDoctor) {
      // only load doctor related surgeons
      if ($loggedInDoctorDetail?.id && !loadedSurgeonsDoctorId) {
        return await loadSurgeonsByDoctorId($loggedInDoctorDetail.id);
      }
    } else if (selectedDoctors && selectedDoctors.length === 1) {
      if (selectedDoctors[0] !== loadedSurgeonsDoctorId)
        return await loadSurgeonsByDoctorId(selectedDoctors[0]);
    } if(selectedDoctors && selectedDoctors.length > 0){
       return await loadSurgeonsByDoctorsId(selectedDoctors);
    } 
    
    else return await searchAllSurgeons();
  };

  const loadSurgeonsByDoctorId = async (accountId) => {
    showLoadingMoreIcon = false;
    isSearching = true;
    await apiMethods.getMethod
      .getAccountDoctors(0, 999, {
        "filter[doctorId]": accountId,
      })
      .then((res) => {
        if (res.error) throw res.error;
        isSearching = false;
        loadedSurgeonsDoctorId = accountId;
        allSurgeons = filterSurgeonResults(res.results);
      })
      .catch((err) => {
        showException(err);
        isSearching = false;
        allSurgeons = [];
      });
  };

  const loadSurgeonsByDoctorsId = async (doctorIds) => {
    showLoadingMoreIcon = false;
    isSearching = true;
    await apiMethods.getMethod
      .getAccountDoctors(0, 999, {
        "filter[doctorId]": doctorIds.join(","),
      })
      .then((res) => {
        if (res.error) throw res.error;
        isSearching = false;
        allSurgeons = filterSurgeonResults(res.results);
      })
      .catch((err) => {
        showException(err);
        isSearching = false;
        allSurgeons = [];
      });
  };

  const searchAllSurgeons = async (index = 0, isMore) => {
    if (!searchKeyWord) return;

    if (isMore) isSearchingMore = true;
    else isSearching = true;

    if (!isMore) allSurgeons = [];

    const searchParams = { name: searchKeyWord };

    if (searchSurgeonInNoGapProgram) searchParams.isNoGapProgram = Number(1);

    await apiMethods.getMethod
      .getAllProviders(index, SEARCH_GAP, searchParams)
      .then(async (res) => {
        handleLoadMore(index, res);
        if (res.error) throw res.error;
        if (res.results.length) {
          if (index === 0) {
            allSurgeons = filterSurgeonResults(res.results);
          } else {
            allSurgeons = filterSurgeonResults([
              ...allSurgeons,
              ...res.results,
            ]);
          }
        }
      })
      .catch((err) => {
        showException(err);
      });

    isSearching = false;
    isSearchingMore = false;
  };

  const handleLoadMore = (index, res) => {
    if (!res?.results?.length || index + res.results.length >= res.total)
      showLoadingMoreIcon = false;
    else {
      showLoadingMoreIcon = true;
      searchIndex = index + SEARCH_GAP;

      const ob = new IntersectionObserver(
        async (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && !isSearchingMore)
              searchAllSurgeons(searchIndex, true);
          }
        },
        {
          threshold: 0,
        },
      );

      if (loadingMoreIconEle) ob.observe(loadingMoreIconEle);
    }
  };

  const filterSurgeonResults = (surgeons) => {
    const filtered = [];

    surgeons.forEach((sur) => {
      sur.uniqueId = sur.uniqueId ?? sur.providerUniqueId;
      if (
        !filtered.some(
          (filteredSurgeon) => filteredSurgeon.uniqueId === sur.uniqueId,
        )
      )
        filtered.push(sur);
    });

    return filtered;
  };

  const toggleChange = (id) => {
    if (selectedSurgeonIds.includes(id))
      selectedSurgeonIds = selectedSurgeonIds.filter((result) => result != id);
    else selectedSurgeonIds.push(id);

    _updateSearchPlaceholder();

    debounceChangeFunc(id);
  };

  const debounceChangeFunc = debounce((lastChangeID) => {
    onChangeFunc(selectedSurgeonIds, { lastChangeID });
  }, SEARCH.DEBOUNCE_TIME);

  const removeSelectedResults = () => {
    selectedSurgeonIds = [];

    _updateSearchPlaceholder();

    const input = document.getElementById(`${inputID}`);

    if (input) input.value = "";

    debounceChangeFunc();
  };

  const _updateSearchPlaceholder = () => {
    if (!selectedSurgeonIds.length) defaultPlaceholder = "";
    else defaultPlaceholder = selectedSurgeonIds.join(", ");
  };

  const getLabel = (id) => {
    if (!id || !allSurgeons || !allSurgeons.length) return undefined;
    const targetOption = allSurgeons.find((op) => op.uniqueId === id);
    return targetOption ? getDisplaySurgeonName(targetOption) : undefined;
  };

  const openList = () => {
    // delayed to bypass the window click event
    setTimeout(() => {
      isOptionsListOpen = true;
      if (
        (selectedDoctors?.length === 1 &&
          loadedSurgeonsDoctorId !== selectedDoctors[0]) ||
        (isUserDoctor && loadedSurgeonsDoctorId !== $loggedInDoctorDetail.id)
      )
        searchAccountDoctors();
    }, 10);
  };

  const filterDisplaySurgeons = (list, selectedDoc, key) => {
    if (selectedDoc?.length === 1 && key) {
      return list.filter((surgeon) =>
        getDisplaySurgeonName(surgeon)
          .toLocaleUpperCase()
          .includes(key.toLocaleUpperCase()),
      );
    }
    return list;
  };

  $: displaySurgeons = filterDisplaySurgeons(
    allSurgeons,
    selectedDoctors,
    searchKeyWord,
  );
</script>

<div class="flex flex-col relative" style="z-index: {zIndex ?? '2'};">
  <p class={defaultTitleClass + " " + titleClass}>{title}</p>

  <div class="w-full relative">
    {#if showSelected && selectedSurgeonIds?.length && !isOptionsListOpen}
      <div
        class="border-b flex flex-row flex-wrap gap-2 {showOptionsWidthClass}"
      >
        {#each selectedSurgeonIds as selectedId}
          <div
            class="flex flex-row items-center px-2 mb-[2px] bg-[#F1F1F2] gap-x-[4px] rounded-[6px] max-w-[158px]"
          >
            <span
              class="truncate text-left flex-1"
              on:click|preventDefault={openList}
            >
              {getLabel(selectedId) ?? selectedId}
            </span>
            <button
              class="btn btn-xs btn-ghost w-[15px] rounded-full p-0 m-0 border-none"
              disabled={isDisabled}
              on:click|preventDefault={(e) => {
                e.stopPropagation();
                toggleChange(selectedId);
              }}
            >
              <i class="fa fa-times" aria-hidden="true" />
            </button>
          </div>
        {/each}
      </div>
    {:else}
      <input
        type="text"
        class={defaultInputClass + " " + inputClass}
        id={inputID}
        placeholder={inputPlaceholder}
        on:click|preventDefault={openList}
        on:input={searchAccountDoctorsDebounce}
        bind:value={searchKeyWord}
        autocomplete="off"
      />
    {/if}
    {#if selectedSurgeonIds.length}
      <div
        class="absolute bottom-1.5 right-4 hover:cursor-pointer hover:text-error"
        on:click={removeSelectedResults}
      >
        X
      </div>
    {/if}
  </div>

  {#if isOptionsListOpen}
    <div
      class={`flex flex-col ${marginTop} max-h-[300px] gap-y-2 w-auto min-w-48 max-w-96 overflow-y-auto absolute border bg-white`}
      id={containerID}
    >
      <div class="flex flex-1 flex-col gap-y-2 p-4 w-full min-w-48 max-w-96">
        {#if isSearching}
          <div class="flex flex-1 gap-x-4 w-48">
            <Loader />
            <p>Searching surgeons</p>
          </div>
        {:else if displaySurgeons.length}
          {#each displaySurgeons as option}
            <div class="flex items-center gap-2 min-h-[42px] h-auto mr-2">
              <input
                id={option.uniqueId}
                type="checkbox"
                class={defaultCheckboxClass + " " + checkboxClass}
                checked={selectedSurgeonIds.includes(option.uniqueId)}
                disabled={isDisabled}
                on:change={() => toggleChange(option.uniqueId)}
              />
              <label
                class="whitespace-nowrap cursor-pointer"
                for={option.uniqueId}
              >
                {getDisplaySurgeonName(option)}
              </label>
            </div>
          {/each}
        {:else}
          <p class="w-48">No options</p>
        {/if}

        <div
          class={`flex flex-1 items-center justify-center ${
            showLoadingMoreIcon && !isSearching ? "" : "hidden"
          }`}
          bind:this={loadingMoreIconEle}
        >
          <Loader />
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  p {
    color: #6f726c;
  }

  input[type="text"] {
    outline: 0;
    border-width: 0 0 2px;
    border-color: #818e9b;
    color: #270053;
  }

  input:focus {
    border-color: #270053;
  }

  label {
    min-width: 3rem;
  }

  .border-b {
    border-width: 0 0 2px;
    border-color: #818e9b;
  }
</style>
